import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import { createDraftMemo } from "@/lib/create-draft-memo";
import { toast } from "@/lib/use-toast";
import type { EventDetail, EventTiny } from "@/types/event";
import type { PatientSidebarCounts } from "@/types/patient-sidebar";
import type { QuickNoteCreate, QuickNoteSummary, QuickNoteUpdate } from "@/types/quick-note";
import type { UserTiny } from "@/types/user";
import { createQuickNote, deleteQuickNote, togglePinQuickNote, updateQuickNote } from "./quick-note";
import { quickNoteQueryKeys } from "./quick-note-query";
import { optimisticAdjustSidebarCounts, revertSidebarCounts } from "./sidebar-cache-utils";
import { invalidateByKey, revertOnError, startOptimisticChange } from "@/api/utils/query-helpers";
import { eventQueryKeys } from "@/query/check-in/calendar/event-query";
type QuickNoteCreateInput = QuickNoteCreate & {
  creator?: UserTiny;
};
type EventCacheSnapshot = {
  key: readonly unknown[];
  previous: EventTiny | EventDetail | (EventTiny | EventDetail)[];
};
function applyEventQuickNote(eventId: string, mutate: (notes: QuickNoteSummary[]) => QuickNoteSummary[]): EventCacheSnapshot[] {
  const snapshots: EventCacheSnapshot[] = [];
  const singleKeys = [eventQueryKeys.tiny(eventId), eventQueryKeys.detail(eventId)];
  for (const key of singleKeys) {
    const prev = queryClient.getQueryData<EventTiny | EventDetail>(key);
    if (!prev) continue;
    snapshots.push({
      key,
      previous: prev
    });
    queryClient.setQueryData(key, {
      ...prev,
      quickNotes: mutate(prev.quickNotes ?? [])
    });
  }
  const arrayQueries = queryClient.getQueriesData<(EventTiny | EventDetail)[]>({
    queryKey: eventQueryKeys.all,
    exact: false
  });
  for (const [key, data] of arrayQueries) {
    if (!Array.isArray(data)) continue;
    const idx = data.findIndex(e => e?.id === eventId);
    if (idx === -1) continue;
    snapshots.push({
      key,
      previous: data
    });
    const next = [...data];
    next[idx] = {
      ...next[idx],
      quickNotes: mutate(next[idx].quickNotes ?? [])
    };
    queryClient.setQueryData(key, next);
  }
  return snapshots;
}
function revertEventCaches(snapshots: EventCacheSnapshot[]) {
  for (const {
    key,
    previous
  } of snapshots) {
    queryClient.setQueryData(key, previous);
  }
}
export const useCreateQuickNoteMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp,
      onMutate: _temp2,
      onError: _temp3,
      onSuccess: _temp4,
      onSettled: _temp5
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useUpdateQuickNoteMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp6,
      onMutate: _temp7,
      onError: _temp8,
      onSettled: _temp9
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useTogglePinQuickNoteMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp10,
      onMutate: _temp11,
      onError: _temp12,
      onSettled: _temp13
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useDeleteQuickNoteMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp14,
      onMutate: _temp15,
      onError: _temp16,
      onSettled: _temp17
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
function _temp(t0) {
  const {
    creator: _creator,
    ...input
  } = t0;
  return createQuickNote(input);
}
async function _temp2(input_0) {
  const key = quickNoteQueryKeys.list(input_0.patientId);
  const optimisticNote = createDraftMemo({
    note: input_0.note,
    patient: {
      id: input_0.patientId
    } as UserTiny,
    creator: input_0.creator ?? {
      id: input_0.creatorId
    } as UserTiny,
    eventId: input_0.eventId,
    isPinned: input_0.isPinned,
    isAlert: input_0.isAlert
  });
  const {
    previous
  } = await startOptimisticChange(key, () => {
    const prev = queryClient.getQueryData(key);
    if (prev) {
      queryClient.setQueryData(key, [optimisticNote, ...prev]);
    }
  });
  const previousSidebarCounts = input_0.eventId ? undefined : optimisticAdjustSidebarCounts(input_0.patientId, {
    memosCount: 1
  });
  const eventCacheSnapshots = input_0.eventId ? applyEventQuickNote(input_0.eventId, notes => [optimisticNote, ...notes]) : undefined;
  return {
    previous,
    key,
    previousSidebarCounts,
    optimisticId: optimisticNote.id,
    eventCacheSnapshots
  };
}
function _temp3(_err, vars, ctx) {
  if (ctx?.key) {
    revertOnError(ctx.key, ctx.previous);
  }
  if (!vars.eventId) {
    revertSidebarCounts(vars.patientId, ctx?.previousSidebarCounts);
  }
  if (ctx?.eventCacheSnapshots) {
    revertEventCaches(ctx.eventCacheSnapshots);
  }
  toast({
    title: "Error",
    description: "Failed to create memo",
    variant: "error"
  });
}
function _temp4(note, vars_0, ctx_0) {
  if (!note || !vars_0.eventId || !ctx_0?.optimisticId) {
    return;
  }
  applyEventQuickNote(vars_0.eventId, notes_0 => notes_0.map(n => n.id === ctx_0.optimisticId ? note : n));
}
function _temp5(_res, _err_0, vars_1) {
  invalidateByKey(quickNoteQueryKeys.list(vars_1.patientId));
}
function _temp6(t0) {
  const {
    noteId,
    input
  } = t0;
  return updateQuickNote(noteId, input);
}
async function _temp7(t0) {
  const {
    noteId: noteId_0,
    input: input_0,
    patientId
  } = t0;
  const key = quickNoteQueryKeys.list(patientId);
  const {
    previous
  } = await startOptimisticChange(key, () => {
    const prev = queryClient.getQueryData(key);
    if (prev) {
      queryClient.setQueryData(key, prev.map(n => n.id === noteId_0 ? {
        ...n,
        ...(input_0.note !== undefined && {
          note: input_0.note
        }),
        ...(input_0.isAlert !== undefined && {
          isAlert: input_0.isAlert
        }),
        updatedDate: new Date().toISOString()
      } : n));
    }
  });
  return {
    previous,
    key
  };
}
function _temp8(_err, _vars, ctx) {
  if (ctx?.key) {
    revertOnError(ctx.key, ctx.previous);
  }
  toast({
    title: "Error",
    description: "Failed to update memo",
    variant: "error"
  });
}
function _temp9(_res, _err_0, vars) {
  invalidateByKey(quickNoteQueryKeys.list(vars.patientId));
}
function _temp10(t0) {
  const {
    noteId
  } = t0;
  return togglePinQuickNote(noteId);
}
async function _temp11(t0) {
  const {
    noteId: noteId_0,
    patientId
  } = t0;
  const key = quickNoteQueryKeys.list(patientId);
  const {
    previous
  } = await startOptimisticChange(key, () => {
    const prev = queryClient.getQueryData(key);
    if (prev) {
      queryClient.setQueryData(key, prev.map(n => n.id === noteId_0 ? {
        ...n,
        isPinned: !n.isPinned
      } : n));
    }
  });
  return {
    previous,
    key
  };
}
function _temp12(_err, _vars, ctx) {
  if (ctx?.key) {
    revertOnError(ctx.key, ctx.previous);
  }
  toast({
    title: "Error",
    description: "Failed to update pin status",
    variant: "error"
  });
}
function _temp13(_res, _err_0, vars) {
  invalidateByKey(quickNoteQueryKeys.list(vars.patientId));
}
function _temp14(t0) {
  const {
    noteId
  } = t0;
  return deleteQuickNote(noteId);
}
async function _temp15(t0) {
  const {
    noteId: noteId_0,
    patientId,
    isEventMemo
  } = t0;
  const key = quickNoteQueryKeys.list(patientId);
  let eventId;
  const {
    previous
  } = await startOptimisticChange(key, () => {
    const prev = queryClient.getQueryData(key);
    if (prev) {
      eventId = prev.find(n => n.id === noteId_0)?.eventId;
      queryClient.setQueryData(key, prev.filter(n_0 => n_0.id !== noteId_0));
    }
  });
  const previousSidebarCounts = isEventMemo ? undefined : optimisticAdjustSidebarCounts(patientId, {
    memosCount: -1
  });
  const eventCacheSnapshots = eventId ? applyEventQuickNote(eventId, notes => notes.filter(n_1 => n_1.id !== noteId_0)) : undefined;
  return {
    previous,
    key,
    previousSidebarCounts,
    eventCacheSnapshots
  };
}
function _temp16(_err, vars, ctx) {
  if (ctx?.key) {
    revertOnError(ctx.key, ctx.previous);
  }
  if (!vars.isEventMemo) {
    revertSidebarCounts(vars.patientId, ctx?.previousSidebarCounts);
  }
  if (ctx?.eventCacheSnapshots) {
    revertEventCaches(ctx.eventCacheSnapshots);
  }
  toast({
    title: "Error",
    description: "Failed to delete memo",
    variant: "error"
  });
}
function _temp17(_ok, _err_0, vars_0) {
  invalidateByKey(quickNoteQueryKeys.list(vars_0.patientId));
}